import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../../utils/errorHandler';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Send, Calendar, Image, Save } from 'lucide-react';
import { Slider } from '../ui/slider';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminMessaging({ config, setConfig, onSave, saving }) {
  const { t } = useLanguage();

  return (
    <div className="border border-border rounded-lg p-6 bg-card space-y-6">
      <div>
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Send className="h-5 w-5" />
          Configuration des Notifications
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Configurez les destinataires des notifications automatiques et la signature email
        </p>
      </div>

      {/* Leave Request Notifications */}
      <div className="space-y-4">
        <h3 className="font-medium flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          Notifications de Congés
        </h3>
        <div className="space-y-2">
          <Label>Email recevant les demandes de congés</Label>
          <Input
            type="email"
            value={config.leave_notification_email || ''}
            onChange={(e) => setConfig({ ...config, leave_notification_email: e.target.value })}
            placeholder="rh@vitalis-sports.ch"
            data-testid="leave-notification-email"
          />
          <p className="text-xs text-muted-foreground">
            Cet email reçoit les demandes et est utilisé comme expéditeur pour les notifications aux collaborateurs
          </p>
        </div>
        
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
          <p className="text-sm text-blue-700 dark:text-blue-400">
            <strong>Notifications automatiques :</strong>
          </p>
          <ul className="text-sm text-blue-600 dark:text-blue-400 mt-2 space-y-1">
            <li>• Nouvelle demande → Email reçu par l'adresse ci-dessus</li>
            <li>• Demande approuvée/rejetée/modifiée → Email envoyé au collaborateur <strong>depuis</strong> l'adresse ci-dessus</li>
          </ul>
        </div>
      </div>

      {/* Email Signature */}
      <div className="space-y-4 pt-4 border-t border-border">
        <h3 className="font-medium flex items-center gap-2">
          <Image className="h-4 w-4" />
          Signature Email
        </h3>
        <p className="text-sm text-muted-foreground">
          Cette signature sera ajoutée automatiquement à tous les emails envoyés par le système
        </p>
        
        {/* Logo URL field */}
        <div className="space-y-2">
          <Label>URL du logo</Label>
          <Input
            type="url"
            value={config.email_signature_logo_url || ''}
            onChange={(e) => setConfig({ ...config, email_signature_logo_url: e.target.value })}
            placeholder="https://vitalis-sports.ch/wp-content/uploads/2024/12/logo_web.png"
            data-testid="email-logo-url"
          />
          <p className="text-xs text-muted-foreground">
            URL de l'image du logo à afficher dans la signature
          </p>
        </div>
        
        {/* Logo Size Control */}
        <div className="space-y-3">
          <Label>Taille du logo dans la signature</Label>
          <div className="flex items-center gap-4">
            <Slider
              value={[config.email_signature_logo_width || 200]}
              onValueChange={(value) => setConfig({ ...config, email_signature_logo_width: value[0] })}
              min={50}
              max={400}
              step={10}
              className="flex-1"
              data-testid="logo-size-slider"
            />
            <span className="text-sm text-muted-foreground w-16 text-right">
              {config.email_signature_logo_width || 200}px
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            Ajustez la largeur du logo (50px - 400px)
          </p>
          {/* Logo preview */}
          {config.email_signature_logo_url && (
            <div className="border border-border rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
              <p className="text-xs text-muted-foreground mb-2">Aperçu du logo :</p>
              <img 
                src={config.email_signature_logo_url} 
                alt="Logo preview" 
                style={{ width: `${config.email_signature_logo_width || 200}px`, height: 'auto' }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          )}
        </div>
        
        <div className="border border-border rounded-lg overflow-hidden">
          <ReactQuill
            theme="snow"
            value={config.email_signature || ''}
            onChange={(value) => setConfig({ ...config, email_signature: value })}
            placeholder="Créez votre signature email..."
            modules={{
              toolbar: [
                [{ 'header': [1, 2, 3, false] }],
                ['bold', 'italic', 'underline'],
                [{ 'color': [] }, { 'background': [] }],
                [{ 'align': [] }],
                ['link', 'image'],
                ['clean']
              ]
            }}
            style={{ minHeight: '200px' }}
          />
        </div>

        {/* Default signature template button */}
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => {
            const logoUrl = config.email_signature_logo_url || 'https://vitalis-sports.ch/wp-content/uploads/2024/12/logo_web.png';
            const logoWidth = config.email_signature_logo_width || 200;
            setConfig({ 
              ...config,
              email_signature_logo_url: logoUrl,
              email_signature_logo_width: logoWidth,
              email_signature: `
<div style="font-family: Arial, sans-serif; color: #333; margin-top: 20px; padding-top: 20px; border-top: 1px solid #ddd;">
  <img src="${logoUrl}" alt="Vitalis Sports" style="width: ${logoWidth}px; height: auto; margin-bottom: 15px;" />
  <p style="margin: 5px 0; font-size: 13px; color: #666;">
    Rorschacherstrasse 43 | 9450 Lüchingen
  </p>
  <p style="margin: 5px 0; font-size: 13px; color: #666;">
    Tel.: +41 71 755 50 66 | <a href="https://www.vitalis-sports.ch" style="color: #1E3A5F;">www.vitalis-sports.ch</a>
  </p>
  <p style="margin: 5px 0; font-size: 13px;">
    <a href="https://www.instagram.com/vitalis_sports/" style="color: #E1306C;">Instagram @vitalis_sports</a>
  </p>
</div>
              `.trim()
            });
          }}
        >
          Charger le modèle par défaut
        </Button>
        
        {/* Button to update logo size in existing signature */}
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => {
            if (config.email_signature) {
              const logoWidth = config.email_signature_logo_width || 200;
              let updatedSignature = config.email_signature;
              
              // Replace img tags to add/update width attribute and style
              updatedSignature = updatedSignature.replace(
                /<img([^>]*)>/gi,
                (match, attrs) => {
                  // Remove existing width/style attributes
                  let cleanAttrs = attrs
                    .replace(/\s*width="[^"]*"/gi, '')
                    .replace(/\s*style="[^"]*"/gi, '');
                  // Add new width and style
                  return `<img${cleanAttrs} width="${logoWidth}" style="width: ${logoWidth}px; height: auto;">`;
                }
              );
              
              setConfig({ ...config, email_signature: updatedSignature });
              toast.success(`Taille du logo mise à jour: ${logoWidth}px. N'oubliez pas d'enregistrer.`);
            }
          }}
          disabled={!config.email_signature}
        >
          Appliquer la taille à la signature
        </Button>

        {/* Preview */}
        {config.email_signature && (
          <div className="space-y-2">
            <Label>Aperçu de la signature</Label>
            <div 
              className="border border-border rounded-lg p-4 bg-white"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(config.email_signature) }}
            />
          </div>
        )}
      </div>

      <div className="flex gap-3 pt-4 border-t border-border">
        <Button
          onClick={() => onSave('messaging')}
          disabled={saving}
          data-testid="save-messaging-config-btn"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? t('saving') : t('save')}
        </Button>
      </div>
    </div>
  );
}
